'use client';

import { TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { ReportInsights } from '@/app/reports/components/report-insights';

type ReportInsightsPanelProps = {
  insights: ReportInsights;
};

const DirectionIcon = ({ direction }: { direction: ReportInsights['direction'] }) => {
  if (direction === 'up') return <TrendingUp className="h-4 w-4 text-emerald-600" />;
  if (direction === 'down') return <TrendingDown className="h-4 w-4 text-rose-600" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
};

const deltaClass = (delta?: number) => {
  if (delta === undefined) return 'text-muted-foreground';
  if (Math.abs(delta) < 2) return 'text-muted-foreground';
  return delta > 0 ? 'text-emerald-600' : 'text-rose-600';
};

const formatDelta = (delta?: number, isPoints?: boolean) => {
  if (delta === undefined) return '—';
  const sign = delta > 0 ? '+' : '';
  const suffix = isPoints ? ' pts' : '%';
  return `${sign}${Math.round(delta * 10) / 10}${suffix}`;
};

export function ReportInsightsPanel({ insights }: ReportInsightsPanelProps) {
  const { t } = useTranslation('reports');

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {insights.stats.map((item) => (
          <div
            key={item.id}
            className="rounded-xl border border-border/80 bg-card px-4 py-3 shadow-sm"
          >
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {t(item.labelKey)}
            </p>
            <div className="mt-2 flex items-end justify-between gap-2">
              <p className="text-2xl font-semibold tabular-nums tracking-tight">{item.display}</p>
              {item.deltaPct !== undefined && (
                <span className={cn('text-xs font-medium tabular-nums', deltaClass(item.deltaPct))}>
                  {formatDelta(item.deltaPct, item.id === 'latest')}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border/80 bg-muted/20 p-4 sm:p-5">
        <div className="mb-3 flex items-center gap-2">
          <DirectionIcon direction={insights.direction} />
          <h3 className="text-sm font-semibold">{t('insights.title')}</h3>
        </div>
        <ul className="space-y-2 text-sm text-muted-foreground">
          {insights.bullets.map((bullet, index) => {
            const params = { ...bullet.params };
            const dir = params.direction;
            if (dir === 'up' || dir === 'down' || dir === 'flat') {
              params.direction = t(`insights.direction.${dir}`);
            }
            return (
              <li key={index} className="flex gap-2">
                <span className="text-primary">•</span>
                <span>{t(bullet.textKey, params)}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
